/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  lazy,
  Suspense
} from 'react'
import { useMediaQuery } from 'usehooks-ts'
import { cn } from '~/lib/utils'
import MediaPause from '~/assets/icons/media-pause'
import MediaPlay from '~/assets/icons/media-play'

// import WavesurferPlayer from '@wavesurfer/react'
const LazyWavesurferPlayer = lazy(() => import('@wavesurfer/react'))
interface PlayerProps {
  children: React.ReactNode
  title?: string
  tag?: string
  description?: string
  music: string
  artwork?: string
  showArtwork: boolean
  colour: string
  align?: 'top' | 'bottom' | undefined
  isStandalone?: boolean
  playing?: boolean
  onFinish?: () => void
  onPlayPause?: (isPlaying: boolean) => void
}

type WaveSurferEvents = {
  audioprocess: () => void
  ready: () => void
  play: () => void
  pause: () => void
  finish: () => void
}

interface WavesurferInstance {
  on: <EventName extends keyof WaveSurferEvents>(
    event: EventName,
    listener: (...args: any[]) => void,
    options?: { once?: boolean; passive?: boolean; capture?: boolean }
  ) => () => void
  playPause: () => void
  setVolume: (volume: number) => void
  getDuration: () => number
  getCurrentTime: () => number
  seekTo: (progress: number) => void
}

const AudioSingle = ({
  children,
  isStandalone,
  music,
  artwork,
  showArtwork,
  colour,
  title,
  tag,
  playing = false,
  onFinish,
  onPlayPause
}: PlayerProps) => {
  const [wavesurfer, setWavesurfer] = useState<WavesurferInstance | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [time, setTime] = useState<number | null>(null)

  const isDesktop = useMediaQuery('(min-width: 768px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  const isMobile = useMediaQuery('(max-width: 640px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  useEffect(() => {
    if (wavesurfer) {
      if (!isStandalone && playing !== isPlaying) {
        wavesurfer.playPause() // Sync playback state with the `playing` prop
        setIsPlaying(playing)
      }
    }
  }, [playing, wavesurfer, isPlaying, isStandalone])

  const onReady = useCallback(
    (ws: WavesurferInstance) => {
      setWavesurfer(ws)
      setIsPlaying(false)
      if (onFinish) {
        ws.on('finish', () => {
          onFinish()

          if (isStandalone) {
            setIsPlaying(false)
            ws.seekTo(0)
          }
        })
      }
    },
    [isStandalone, onFinish]
  )

  const handlePlayPause = useCallback(() => {
    if (wavesurfer) {
      wavesurfer.playPause()
      const newIsPlaying = !isPlaying
      setIsPlaying(newIsPlaying)
      if (onPlayPause) {
        onPlayPause(newIsPlaying) // Notify parent about play/pause state
      }
    }
  }, [wavesurfer, isPlaying, onPlayPause])

  const duration = wavesurfer && wavesurfer.getDuration()

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.on('audioprocess', () => {
        setTime(wavesurfer.getCurrentTime())
      })
    }
  }, [wavesurfer])

  const formatTimecode = (seconds: number) => {
    return new Date(seconds * 1000).toISOString().substring(14, 19)
  }

  const compColours = useMemo(() => {
    switch (colour) {
      case 'chartreuse':
        return {
          waveColour: '#f7fde2',
          progressColour: '#d7f777',
          iconColur: '#0e1647',
          buttonBg: 'bg-white'
        }
      case 'ink-blue':
        return {
          waveColour: '#cfd0da',
          progressColour: '#0e1647',
          iconColur: '#ffffff',
          buttonBg: 'bg-blue-d'
        }

      case 'mid-blue':
        return {
          waveColour: '#d2dbfc',
          progressColour: '#1d4cf2',
          iconColur: '#ffffff',
          buttonBg: 'bg-blue-m'
        }

      case 'white':
        return {
          waveColour: '#9fa2b5',
          progressColour: '#ffffff',
          iconColur: '#0e1647',
          buttonBg: 'bg-white'
        }

      default:
        return {
          waveColour: '#cfd0da',
          progressColour: '#0e1647',
          iconColur: '#d7f777'
        }
    }
  }, [colour])

  return (
    <div
      className={cn(!isStandalone ? '' : 'gap-xs sm:gap-s', 'flex flex-col')}
    >
      <div className='gap-m flex items-start'>
        {isDesktop && showArtwork && (
          <div className='bg-blue-d size-xl md:size-2xl lg:size-3xl shrink-0'>
            <img
              src={artwork}
              alt={title || 'Artwork'}
              className='h-full w-full object-cover'
              width={120}
              height={120}
              loading='lazy'
            />
          </div>
        )}

        <div className='space-y-m w-full'>
          {!isDesktop && (
            <div className='flex items-center justify-between'>
              <button
                onClick={handlePlayPause}
                className={cn(
                  compColours.buttonBg,
                  // isPlaying ? 'border-white text-white' : 'border-blue-d',
                  'size-l lg:size-xl flex cursor-pointer items-center justify-center rounded-full'
                )}
                aria-label='Play/Pause'
              >
                {isPlaying ? (
                  <MediaPause
                    fill={compColours.iconColur}
                    width='1.5em'
                    height='1.5em'
                    strokewidth={4}
                  />
                ) : (
                  <MediaPlay
                    fill={compColours.iconColur}
                    width='1.5em'
                    height='1.5em'
                    strokewidth={4}
                  />
                )}
              </button>
              <div className='timecode w-l'>
                <span>
                  {isPlaying
                    ? time !== null
                      ? formatTimecode(time)
                      : '00:00'
                    : duration !== null
                      ? formatTimecode(duration)
                      : '00:00'}
                </span>
              </div>
            </div>
          )}
          <div className='gap-s flex items-end'>
            {isDesktop && (
              <button
                onClick={handlePlayPause}
                className={cn(
                  compColours.buttonBg,
                  // isPlaying ? 'border-white text-white' : 'border-blue-d',
                  'size-l lg:size-xl flex cursor-pointer items-center justify-center rounded-full'
                )}
                aria-label='Play/Pause'
              >
                {isPlaying ? (
                  <MediaPause
                    fill={compColours.iconColur}
                    width='1.5em'
                    height='1.5em'
                    strokewidth={4}
                  />
                ) : (
                  <MediaPlay
                    fill={compColours.iconColur}
                    width='1.5em'
                    height='1.5em'
                    strokewidth={4}
                  />
                )}
              </button>
            )}
            <div className='grow'>
              <Suspense fallback={<div>Loading audio...</div>}>
                <LazyWavesurferPlayer
                  height={64}
                  waveColor={compColours.waveColour}
                  url={music}
                  onReady={onReady}
                  barWidth={isDesktop ? 12 : isMobile ? 4 : 6}
                  barGap={isDesktop ? 6 : isMobile ? 2 : 4}
                  barRadius={0}
                  barAlign='bottom'
                  progressColor={compColours.progressColour}
                  normalize={true}
                  cursorWidth={0}
                  cursorColor='#0e1647'
                  mediaControls={false}
                />
              </Suspense>
            </div>
            {isDesktop && (
              <div className='timecode w-l'>
                <span>
                  {isPlaying
                    ? time !== null
                      ? formatTimecode(time)
                      : '00:00'
                    : duration !== null
                      ? formatTimecode(duration)
                      : '00:00'}
                </span>
              </div>
            )}
          </div>
          <div className='space-x-xs flex'>
            {!isDesktop && showArtwork && (
              <div className='bg-blue-d size-xl md:size-2xl lg:size-3xl shrink-0'>
                <img
                  src={artwork}
                  alt={title || 'Artwork'}
                  className='h-full w-full object-cover'
                  width={120}
                  height={120}
                  loading='lazy'
                />
              </div>
            )}

            <div className='space-y-xs flex grow flex-col'>
              {tag && <p className='tag'>{tag}</p>}
              {isStandalone && children}
            </div>
          </div>
        </div>
      </div>

      {!isStandalone && (
        <div className='gap-m flex items-start'>
          {isDesktop && /* isStandalone && */ showArtwork && (
            <div className='size-xl md:size-2xl lg:size-3xl shrink-0'></div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}

export default AudioSingle
